import { ChevronDownIcon, CloseIcon } from '@chakra-ui/icons';
import { Button, HStack, Heading, Menu, MenuButton, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Text } from '@chakra-ui/react';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { MakeGroupAdmin } from 'v4/store/actions/global.data.actions';

const ExitGroupPopup = ({
  onClose,
  isExitGroupPopup,
  handleExitMessage,
  isAdmin,
  groupMemberDetail,
  onAssignAdmin,
  roomId,
}) => {
  const adminCount = groupMemberDetail?.filter(
    (member) => member?.role === 'admin'
  )?.length;
  const nonAdminMembers =
    groupMemberDetail?.filter((member) => member?.role !== 'admin') || [];
  const isSingleAdmin = adminCount === 1 && isAdmin;
  const { t } = useTranslator();
  const dispatch = useDispatch();
  const [selectedNewAdmin, setSelectedNewAdmin] = useState('');
  const [selectedNewAdminName, setSelectedNewAdminName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAssignAndExit = async () => {
    if (!selectedNewAdmin) return;
    setIsSubmitting(true);
    try {
      const res = await dispatch(
        MakeGroupAdmin({ room_id: roomId, user_id: selectedNewAdmin })
      );
      if (!res?.error) {
        handleExitMessage?.();
      } else {
        console.error('MakeGroupAdmin failed:', res?.error);
      }
    } catch (error) {
      console.error('Error assigning admin:', error);
    } finally {
      setIsSubmitting(false);
      onClose?.();
    }
  };

  return (
    <>
      <Modal isOpen={isExitGroupPopup} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent
          backgroundColor="#FFFFFF"
          padding="5px 5px 0px 5px"
          maxWidth="436px"
          borderRadius="16px"
        >
          <ModalHeader>
            <HStack alignItems="center" justifyContent="space-between">
              <Heading
                fontSize="15px"
                fontFamily="Inter"
                lineHeight="18px"
                fontWeight={600}
                color="#5B5B5B"
              >
                {isSingleAdmin && nonAdminMembers.length > 0
                  ? t('myMessages.assignNewAdmin') || 'Assign a new admin'
                  : t('myMessages.exitGroup') || 'Exit Group'}
              </Heading>
              <CloseIcon
                cursor="pointer"
                width="10px"
                height="10px"
                color="#5B5B5B"
                fontWeight={600}
                onClick={() => onClose()}
              />
            </HStack>
          </ModalHeader>
          <ModalBody>
            <Text maxWidth="388px" w="100%">
              {isSingleAdmin && nonAdminMembers.length > 0
                ? t('myMessages.onlyAdminWarning') ||
                  'You are the only admin of this group. To ensure continued management, please assign a new admin before leaving.'
                : t('myMessages.confirmExitGroup') ||
                  'Are you sure you want to exit this group?'}
            </Text>

            {isSingleAdmin && nonAdminMembers.length > 0 && (
              <Menu w="100%">
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon color="#5B5B5B" />}
                  w="100%"
                  mt={3}
                  textAlign="left"
                  fontSize="14px"
                  fontWeight={400}
                  color={selectedNewAdminName ? '#000000' : '#747474'}
                  backgroundColor="#F8F9FA"
                  border="1px solid #E4E6EB"
                  borderRadius="8px"
                  justifyContent="space-between"
                  _hover={{ backgroundColor: '#F0F2F5' }}
                  _active={{ backgroundColor: '#E4E6EB' }}
                >
                  <Text noOfLines={1} textAlign="left">
                    {selectedNewAdminName || 'Select a member to make admin...'}
                  </Text>
                </MenuButton>
                <MenuList maxH="200px" overflowY="auto" zIndex={2000} w="100%">
                  {nonAdminMembers.map((member) => {
                    const memberId = member?.user?.id || member?.id;
                    const memberName = member?.user?.first_name
                      ? `${member.user.first_name} ${
                          member.user.last_name || ''
                        }`.trim()
                      : member?.user?.email || member?.display_name || 'Member';
                    return (
                      <MenuItem
                        key={memberId}
                        onClick={() => {
                          setSelectedNewAdmin(memberId);
                          setSelectedNewAdminName(memberName);
                        }}
                        fontSize="14px"
                      >
                        {memberName}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Menu>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button
                backgroundColor="transparent"
                border="1px solid #D9D9D9"
                borderRadius="76px"
                p="9px 16px"
                fontSize="12px"
                fontWeight={500}
                lineHeight="14.4px"
                color="#3D5A80"
                _hover={{
                  backgroundColor: 'transparent',
                }}
                onClick={() => {
                  onClose();
                }}
              >
                {t('buttons.cancel') || 'Cancel'}
              </Button>

              {isSingleAdmin && nonAdminMembers.length > 0 ? (
                <>
                  {onAssignAdmin && (
                    <Button
                      backgroundColor="transparent"
                      border="1px solid #3D5A80"
                      borderRadius="76px"
                      p="9px 16px"
                      fontSize="12px"
                      fontWeight={500}
                      lineHeight="14.4px"
                      color="#3D5A80"
                      onClick={() => {
                        onClose();
                        onAssignAdmin();
                      }}
                    >
                      {`${t('pageJobs.manage')} ${t('myMessages.members') || 'Members'}`}
                    </Button>
                  )}
                  {selectedNewAdmin && (
                    <Button
                      backgroundColor="#3D5A80"
                      border="1px solid #D9D9D9"
                      borderRadius="76px"
                      p="9px 16px"
                      fontSize="12px"
                      fontWeight={500}
                      lineHeight="14.4px"
                      color="#FFFFFF"
                      isLoading={isSubmitting}
                      _hover={{
                        backgroundColor: '#3D5A80',
                      }}
                      onClick={handleAssignAndExit}
                    >
                      {`Assign & ${t('myMessages.exitGroup') || 'Exit Group'}`}
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  backgroundColor="#3D5A80"
                  border="1px solid #D9D9D9"
                  borderRadius="76px"
                  p="9px 14px"
                  fontSize="12px"
                  fontWeight={500}
                  lineHeight="14.4px"
                  color="#FFFFFF"
                  _hover={{
                    backgroundColor: '#3D5A80',
                  }}
                  onClick={() => handleExitMessage()}
                >
                  {t('myMessages.exit') || 'Exit'}
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ExitGroupPopup;
